import { useState } from "react";
import { Share2, MessageSquare, Copy, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface ShareButtonProps {
  content: string;
}

const SITE_URL = "shhhbookclub.com";

/**
 * Extracts book titles and authors from the AI response.
 */
function extractBooks(content: string): { title: string; author: string }[] {
  const books: { title: string; author: string }[] = [];
  const patterns = [
    /\*\*(.+?)\*\*\s+by\s+(.+?)(?:\n|$|\.|\s—|\s–|\s-\s|\()/gi,
    /"(.+?)"\s+by\s+(.+?)(?:\n|$|\.|\s—|\s–|\s-\s|\()/gi,
  ];
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(content)) !== null) {
      const title = match[1].trim().replace(/^\*+|\*+$/g, "");
      const author = match[2].trim().replace(/[,;]$/, "");
      if (title.length > 1 && author.length > 1 && !books.some((b) => b.title.toLowerCase() === title.toLowerCase())) {
        books.push({ title, author });
      }
    }
  }
  return books.slice(0, 3);
}

/** Short, punchy share text for SMS / clipboard */
function buildShareText(content: string): string {
  const books = extractBooks(content);

  if (books.length > 0) {
    const bookLines = books
      .map((b) => `"${b.title}" by ${b.author}`)
      .join(", ");
    return `ok you NEED to read ${bookLines} — found on ${SITE_URL} ✨📚`;
  }

  const sentences = content
    .replace(/[#*_~`>\[\]()]/g, "")
    .split(/(?<=[.!?])\s+/)
    .filter((s) => s.length > 20 && s.length < 140);

  const best = sentences[0] || content.slice(0, 100).trim() + "…";
  return `"${best.trim()}" — ${SITE_URL} ✨📚`;
}

/** Richer text for story / social posts */
function buildSocialText(content: string): string {
  const books = extractBooks(content);

  if (books.length > 0) {
    const bookLines = books
      .map((b) => `📖 "${b.title}" — ${b.author}`)
      .join("\n");
    return `✨ just got the best rec from Shhh… Book Club\n\n${bookLines}\n\nindie books that actually hit different\n${SITE_URL}`;
  }

  const sentences = content
    .replace(/[#*_~`>\[\]()]/g, "")
    .split(/(?<=[.!?])\s+/)
    .filter((s) => s.length > 30 && s.length < 200);

  const best = sentences[0] || content.slice(0, 150).trim() + "…";
  return `✨ found this on Shhh… Book Club\n\n"${best.trim()}"\n\n${SITE_URL}`;
}

const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
const canNativeShare = () => typeof navigator.share === "function";

export default function ShareButton({ content }: ShareButtonProps) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const copyToClipboard = async (text: string, successMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(successMsg);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — try selecting the text manually.");
    }
  };

  const shareViaSMS = () => {
    const text = buildShareText(content);
    // Use just ?body= for best cross-platform compatibility (iOS + Android)
    const smsUri = `sms:${isMobile() ? "&" : "?&"}body=${encodeURIComponent(text)}`;
    window.location.href = smsUri;
    setOpen(false);
  };

  const shareToInstagram = async () => {
    const text = buildSocialText(content);

    // Copy text first, then open Instagram DMs directly
    await copyToClipboard(text, "Copied! Opening Instagram DMs — just paste it 📸");
    setTimeout(() => {
      if (isMobile()) {
        // Try deep link to Instagram DM inbox on mobile
        window.location.href = "instagram://direct-inbox";
      } else {
        window.open("https://www.instagram.com/direct/inbox/", "_blank");
      }
      setOpen(false);
    }, 600);
  };

  const shareToTikTok = async () => {
    const text = buildSocialText(content);

    // On mobile, use native share sheet — user can pick TikTok
    if (canNativeShare()) {
      try {
        await navigator.share({ text, url: `https://${SITE_URL}` });
        setOpen(false);
        return;
      } catch (e) {
        if ((e as Error)?.name === "AbortError") {
          setOpen(false);
          return;
        }
      }
    }

    // Desktop fallback: copy + open TikTok
    await copyToClipboard(text, "Copied! Opening TikTok — paste into your post 🎬");
    setTimeout(() => {
      window.open("https://www.tiktok.com/", "_blank");
      setOpen(false);
    }, 600);
  };

  const handleCopy = () => {
    const text = buildSocialText(content);
    copyToClipboard(text, "Copied — paste it anywhere ✨");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="group mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-all hover:border-primary/40 hover:bg-primary/10 hover:shadow-sm"
          aria-label="Share this recommendation"
        >
          <Share2 size={13} className="transition-transform group-hover:scale-110" />
          <span>share this find</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 rounded-2xl border border-border/60 bg-card p-2 shadow-xl"
      >
        <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Pass it along
        </p>

        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2.5 rounded-xl text-sm font-medium"
          onClick={shareViaSMS}
        >
          <MessageSquare size={15} />
          Text a friend
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2.5 rounded-xl text-sm font-medium"
          onClick={shareToInstagram}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-[15px] w-[15px]">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
          </svg>
          Instagram
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2.5 rounded-xl text-sm font-medium"
          onClick={shareToTikTok}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-[15px] w-[15px]">
            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13a8.28 8.28 0 005.58 2.15V11.7a4.79 4.79 0 01-3.77-1.78V6.69h3.77z" />
          </svg>
          TikTok
        </Button>

        <div className="my-1.5 h-px bg-border/30" />

        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2.5 rounded-xl text-sm font-medium text-muted-foreground"
          onClick={handleCopy}
        >
          {copied ? <Check size={15} className="text-primary" /> : <Copy size={15} />}
          {copied ? "Copied ✨" : "Copy to clipboard"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
